import styled from 'styled-components'
import { ChevronLeftIcon, ChevronRightIcon } from '@shipt/design-system-icons'
import { NextLink } from '@/features/shared/elements/Link'
import { Row } from '@shipt/design-system-layouts'
import { IconButton } from '@shipt/design-system-buttons'
import { Body } from '@shipt/design-system-typography'
import { screenSizes } from '@shipt/design-system-themes'
import { Fragment } from 'react'
import { ButtonLink } from '@/features/shared/elements/Link/Buttons'

const PaginationButton = ({
  page,
  hideOnMobile,
  shallow,
  url,
  isCurrentPage,
}: {
  page: number
  shallow: boolean
  hideOnMobile: boolean
  url: string
  isCurrentPage: boolean
}) => {
  return (
    <ButtonLink
      prefetch={false}
      variant={isCurrentPage ? 'secondary' : 'tertiary'}
      href={url}
      aria-label={`Page ${page}`}
      size="sm"
      shallow={shallow}
      className={hideOnMobile ? 'hideOnMobile' : undefined}
    >
      {page}
    </ButtonLink>
  )
}

export const Pagination = ({
  totalPages,
  currentPage,
  generatePageUrl,
  shallow,
  suppressBoundaries = false,
}: {
  shallow: boolean
  totalPages: number
  currentPage: number
  generatePageUrl: (page: number) => string
  suppressBoundaries?: boolean
}) => {
  const pages = Array.from({ length: totalPages }, (_, i) => i + 1)
  const isFirstPage = currentPage === 1
  const isLastPage = currentPage === totalPages

  const LeftArrowProps = isFirstPage
    ? { disabled: true }
    : {
        forwardedAs: NextLink,
        prefetch: false,
        href: generatePageUrl(currentPage - 1),
        shallow,
      }
  const RightArrowProps = isLastPage
    ? { disabled: true }
    : {
        forwardedAs: NextLink,
        prefetch: false,
        href: generatePageUrl(currentPage + 1),
        shallow,
      }

  function renderPageItem(page: number) {
    const showPage5 = suppressBoundaries && page === 5 && currentPage < 3
    const showPageTotalMinus4 =
      suppressBoundaries && page === totalPages - 4 && currentPage > totalPages - 2

    const isBoundaryPage = page === 1 || page === totalPages
    const isAdjacentToCurrent = page === currentPage - 1 || page === currentPage + 1
    const isEdgeCasePage =
      (page === 3 && currentPage <= 2) || (page === totalPages - 2 && currentPage >= totalPages - 1)
    const isEllipsisPage =
      page === currentPage + 2 ||
      page === currentPage - 2 ||
      (currentPage === 1 && page === 4) ||
      (currentPage === totalPages && page === totalPages - 3)

    if (page === currentPage) {
      return (
        <PaginationButton
          hideOnMobile={false}
          key={page}
          page={page}
          isCurrentPage
          url={generatePageUrl(page)}
          shallow={shallow}
        />
      )
    } else if (isBoundaryPage && !suppressBoundaries) {
      return (
        <PaginationButton
          hideOnMobile={false}
          key={page}
          page={page}
          isCurrentPage={false}
          url={generatePageUrl(page)}
          shallow={shallow}
        />
      )
    } else if (isAdjacentToCurrent) {
      return (
        <Fragment key={page}>
          <PaginationButton
            page={page}
            isCurrentPage={false}
            url={generatePageUrl(page)}
            shallow={shallow}
            hideOnMobile={!suppressBoundaries}
          />
          {!suppressBoundaries && <Ellipses className="hideOnTablet">&hellip;</Ellipses>}
        </Fragment>
      )
    } else if (isEdgeCasePage) {
      return (
        <PaginationButton
          url={generatePageUrl(page)}
          isCurrentPage={false}
          hideOnMobile={!suppressBoundaries}
          page={page}
          key={page}
          shallow={shallow}
        />
      )
    } else if (isEllipsisPage) {
      if (suppressBoundaries) {
        return (
          <PaginationButton
            url={generatePageUrl(page)}
            isCurrentPage={false}
            hideOnMobile={false}
            page={page}
            key={page}
            shallow={shallow}
          />
        )
      } else {
        return (
          <Ellipses key={page} className="hideOnMobile">
            &hellip;
          </Ellipses>
        )
      }
    } else if (showPage5 || showPageTotalMinus4) {
      return (
        <PaginationButton
          url={generatePageUrl(page)}
          isCurrentPage={false}
          hideOnMobile={false}
          page={page}
          key={page}
          shallow={shallow}
        />
      )
    }
    return null
  }

  return (
    <PaginationWrapper marginTop="4xl" justify="center" spacing="md">
      <IconButton
        icon={ChevronLeftIcon}
        aria-label="Previous Page"
        size="sm"
        variant="tertiary"
        {...LeftArrowProps}
      />
      {pages.map(renderPageItem)}
      <IconButton
        icon={ChevronRightIcon}
        {...RightArrowProps}
        aria-label="Next Page"
        size="sm"
        variant="tertiary"
      />
    </PaginationWrapper>
  )
}

const Ellipses = styled(Body)`
  align-self: end;
`

const PaginationWrapper = styled(Row)`
  @media ${screenSizes.max_mobile} {
    .hideOnMobile {
      display: none;
    }
  }

  @media ${screenSizes.tablet} {
    .hideOnTablet {
      display: none;
    }
  }
`
